import { skipToken, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import type { NavigationScreenState } from '@gitbook/app-navigation';
import { Icon } from '@gitbook/spine-icons';
import { Button, toast } from '@gitbook/spine-react';
import { assertNever } from '@gitbook/util-ts';

import { useHistoryLocation, useNavigationState } from '../../navigation';
import { apiQuery } from '../../state';
import {
    checkImportRunMatchesTarget,
    IMPORT_RUN_POLL_INTERVAL,
    type ImportRunTargetFilter,
    isImportRunActive,
    isImportRunTerminal,
    mergeImportRun,
} from './importRuns';
import {
    forgetStartedImportRun,
    getLiveImportRunsSnapshot,
    getReportedImportRunsSnapshot,
    getStartedImportRunsSnapshot,
    retainImportRunChannel,
    setReportedImportRun,
    subscribeToLiveImportRuns,
    subscribeToStartedImportRuns,
} from './importRunsStore';

/**
 * Toasts for the imports started in this tab.
 *
 * Deliberately scoped to this tab: a toast is feedback for an action someone took here, so it does
 * not follow them to another device the way the import itself now does.
 */
export function ImportRunToasts() {
    const { screen } = useNavigationState();
    const location = useHistoryLocation();
    const toastScreenKey = getToastScreenKey(screen, location.key);
    const started = useSyncExternalStore(
        subscribeToStartedImportRuns,
        getStartedImportRunsSnapshot
    );
    const live = useSyncExternalStore(subscribeToLiveImportRuns, getLiveImportRunsSnapshot);
    const reported = useSyncExternalStore(subscribeToLiveImportRuns, getReportedImportRunsSnapshot);
    const trackedRunIdsRef = useRef<Set<string>>(new Set());
    const completedToastLocationsRef = useRef<Map<string, string>>(new Map());

    useEffect(() => {
        for (const [toastId, screenKey] of completedToastLocationsRef.current) {
            if (screenKey !== toastScreenKey) {
                toast.dismiss(toastId);
                completedToastLocationsRef.current.delete(toastId);
            }
        }
    }, [toastScreenKey]);

    // The imports started here are few, and only ever the ones this tab requested.
    useEffect(() => {
        const releases = started.map((run) => retainImportRunChannel(run.id));
        return () => {
            releases.forEach((release) => release());
        };
    }, [started]);

    const { data: changeRequest } = useQuery(
        apiQuery(
            'getChangeRequestById',
            screen.type === 'space' && screen.changeRequest && started.length > 0
                ? {
                      spaceId: screen.space,
                      changeRequestId: String(screen.changeRequest),
                  }
                : skipToken
        )
    );

    const screenSpaceId = screen.type === 'space' ? screen.space : null;
    const screenChangeRequestId = changeRequest?.id;
    const activeTarget = useMemo(
        (): ImportRunTargetFilter | null =>
            screenSpaceId
                ? {
                      space: screenSpaceId,
                      changeRequest: screenChangeRequestId ?? null,
                  }
                : null,
        [screenSpaceId, screenChangeRequestId]
    );

    useEffect(() => {
        for (const trace of started) {
            // The server has the last word on whether the run is still going: a workflow that died
            // leaves its channel replaying `in-progress` forever.
            const run = mergeImportRun(reported.get(trace.id), live.get(trace.id));
            if (!run) {
                continue;
            }

            // Nothing more will happen to a run that ended, so stop tracking it and let its
            // channel go. Done before the branches below so it covers every run, including runs
            // already terminal the first time they are seen.
            if (isImportRunTerminal(run)) {
                forgetStartedImportRun(run.id);
            }

            const target = run.target ?? trace.target;
            const isForActiveTarget =
                activeTarget && checkImportRunMatchesTarget({ ...run, target }, activeTarget);

            const toastId = `import-run-${run.id}`;
            const action = (
                <Button
                    kind={run.status === 'completed' ? 'primary' : 'secondary'}
                    label={
                        target.changeRequest
                            ? run.status === 'completed'
                                ? 'Review changes'
                                : 'View change request'
                            : 'View space'
                    }
                    trailingIcon={run.status === 'completed' ? Icon.ArrowRight : undefined}
                    href={{
                        current: {
                            type: 'space',
                            organization: trace.organizationId,
                            site: trace.site ?? undefined,
                            space: target.space,
                            page: '',
                            changeRequest: target.changeRequest,
                            mode:
                                target.changeRequest && run.status === 'completed'
                                    ? 'overview'
                                    : undefined,
                        },
                    }}
                    onPress={() => {
                        analytics.logEvent({
                            type: 'import_open',
                            trigger: 'toast',
                            status: run.status,
                        });
                    }}
                />
            );

            switch (run.status) {
                case 'failed': {
                    if (trackedRunIdsRef.current.has(run.id)) {
                        toast.error('Error while importing your content', null, {
                            id: toastId,
                            description:
                                run.reason ??
                                'Something went wrong while importing your content. Please try again.',
                            action,
                        });
                        trackedRunIdsRef.current.delete(run.id);
                    }
                    break;
                }
                case 'completed': {
                    if (trackedRunIdsRef.current.has(run.id)) {
                        completedToastLocationsRef.current.set(toastId, toastScreenKey);
                        toast.success('Your import is complete', {
                            id: toastId,
                            description: (
                                <div className="flex min-w-0 flex-col items-start gap-3">
                                    <div>
                                        Review the imported content before publishing your changes.
                                    </div>
                                    {action}
                                </div>
                            ),
                            persist: true,
                            onDismiss: () => {
                                completedToastLocationsRef.current.delete(toastId);
                            },
                        });
                        trackedRunIdsRef.current.delete(run.id);
                    }
                    break;
                }
                case 'cancelled': {
                    if (trackedRunIdsRef.current.has(run.id)) {
                        toast.dismiss(toastId);
                        trackedRunIdsRef.current.delete(run.id);
                    }
                    break;
                }
                case 'in-progress':
                case 'pending': {
                    trackedRunIdsRef.current.add(run.id);
                    if (trace.site || isForActiveTarget) {
                        toast.dismiss(toastId);
                    } else {
                        toast.loading('Importing content…', {
                            id: toastId,
                            action,
                        });
                    }
                    break;
                }
                default:
                    assertNever(run.status);
            }
        }
    }, [started, live, reported, activeTarget, toastScreenKey]);

    return <ImportRunToastsWatchdog started={started} live={live} reported={reported} />;
}

/**
 * Poll the runs this tab is waiting on that have gone quiet.
 *
 * A workflow that dies stops publishing, and its channel keeps replaying the last thing it said, so
 * the toast would spin forever. The server reports such a run as failed once it outlives its
 * deadline — this is what asks.
 */
function ImportRunToastsWatchdog(props: {
    started: ReturnType<typeof getStartedImportRunsSnapshot>;
    live: ReadonlyMap<string, api.ContentImportRun>;
    reported: ReadonlyMap<string, api.ContentImportRun>;
}) {
    const { started, live, reported } = props;

    // Only the one that has been waiting longest: two imports stuck at once in the same tab is not
    // worth a query each. The organization comes from the run rather than the screen, since the
    // user may well have navigated to another one by the time it goes quiet.
    const pendingRun = started.find((trace) => {
        const run = mergeImportRun(reported.get(trace.id), live.get(trace.id));
        return !run || isImportRunActive(run);
    });

    const { data } = useQuery({
        ...apiQuery(
            'getImportRun',
            pendingRun
                ? { organizationId: pendingRun.organizationId, importRunId: pendingRun.id }
                : skipToken
        ),
        refetchInterval: IMPORT_RUN_POLL_INTERVAL,
    });

    useEffect(() => {
        if (data) {
            setReportedImportRun(data);
        }
    }, [data]);

    return null;
}

/**
 * Where a completed toast belongs: on a space, the space, change request and tab, so the editor
 * canonicalizing the page path does not dismiss it; elsewhere, the exact location.
 */
function getToastScreenKey(screen: NavigationScreenState, locationKey: string) {
    if (screen.type !== 'space') {
        return locationKey;
    }
    return `space:${screen.space}:${screen.changeRequest ?? ''}:${screen.mode ?? ''}`;
}
